import { projectTypesInversed, ProjectTypesKeys } from '@teinor/erp/types/company/customer/customerProject'
import { useCallback, useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { ConnectedProps, connect } from 'react-redux'
import { useParams } from 'react-router-dom'
import { CustomerProjectAddModelTypeExtended } from '../../../../../app/erp/screens/Company/CustomerParent/CustomerProjects/types'
import { ModalOpenedSetTy } from '../../../../../baseComponents/Modal/types'
import { MediumModalCreateEdit } from '../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../../baseComponents/ModalsLayouts/types'
import { emptyExtraLineFirstSortFunction } from '../../../../../forms/CustomSelect/functions'
import { FormRender } from '../../../../../forms/FormRenderer/types'
import { BeforeSubmitHandler } from '../../../../../forms/TwinForm/types'
import { addDictionaryExtraLine } from '../../../../../utils/globals/dictionary'
import withLoading from '../../../../../utils/hoc/withLoading'
import { useGetCustomerProjectTypeByIsMain, useParseCustomerProjectTypeByStatus } from '../../../../../utils/hooks/customer/useProjectsAndOpportunitiesDict'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../utils/reducers'
import { EmployeesPayload } from '../../../../../utils/reducers/company/employees'
import { TaskCFieldPayload } from '../../../../../utils/reducers/company/taskCFields'
import { CustomerProjectTypePayload, DictionaryCustomerProject } from '../../../../../utils/reducers/customers/customerProjectTypes'
import { TChecklistsPayload } from '../../../../../utils/reducers/customers/tChecklists'
import { getUserInfo } from '../../../../../utils/reducers/getters'
import { GroupPayload } from '../../../../../utils/reducers/groups/groups'
import { sortCustomerProjectStatuses } from '../../../SortCustomerProjectStatuses'

type ModalCreateCustomerOpportunityProps = ModalEditComponentProps & ReduxCustomerEmployeeGroup & {}

const ModalCreateCustomerOpportunity: React.FC<ModalCreateCustomerOpportunityProps> = ({ allRowData, onSubmit, setOpened, employees, groups, basicData, stopPropagation, customerProjectTypes, tChecklists }) => {
    const { t } = useTwinTranslation()
    const { id } = useParams()
    const myEmployee = getUserInfo()
    const CustomerId = basicData?.CustomerId || id
    const { myProjectStatuses, setMyProjectTypeId, handleBeforeSubmit, myParsedCustomerProjectTypes, myProjectTypeId } = useModalCreateCustomerOpportunityLogic({ id: allRowData?.CustomerProjectTypeId, customerProjectTypes })
    const checkListExtraLineDict = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(tChecklists || {}))), [tChecklists])
    const fields: FormRender<CustomerProjectAddModelTypeExtended> = [
        {
            cols: 2,
            elements: [
                {
                    name: 'name',
                    component: 'InputWithLabelMargin',
                    label: t('name', 'Nombre'),
                    required: true
                },
                {
                    name: 'description',
                    component: 'TextArea',
                    label: t('description', 'Descripción'),
                },
                {
                    name: 'CustomerProjectTypeId',
                    component: 'CustomSelectWithSearchBar',
                    required: true,
                    value: myProjectTypeId,
                    label: t('opportunityType', 'Tipo de oportunidad'),
                    items: myParsedCustomerProjectTypes,
                    onChange: (value) => setMyProjectTypeId(value)
                },
                {
                    name: 'CurrentCProjectStatusId',
                    component: 'CustomSelectColor',
                    label: t('projectStatus', 'Tipo de estado'),
                    items: myProjectStatuses || {},
                    value: customerProjectTypes?.[parseInt(myProjectTypeId || '0')]?.CustomerProjectStatusesByType?.opportunity?.[0]?.id || '',
                    extraDropDownParams: {
                        sortFunction: sortCustomerProjectStatuses
                    }
                },
                {
                    name: 'TChecklistId',
                    component: 'CustomSelectWithSearchBar',
                    label: t('defaultList', 'Lista por defecto'),
                    items: checkListExtraLineDict || {},
                    value: myParsedCustomerProjectTypes[myProjectTypeId || 0]?.CPChecklistId,
                    sortFunction: emptyExtraLineFirstSortFunction
                },
                {
                    name: 'limit_date',
                    label: t('limitDate', 'Fecha límite'),
                    component: 'InputCalendarStateFull',
                    onlyValids: true,
                },
                {
                    name: 'CustomerId',
                    component: 'InputHidden',
                    value: CustomerId
                },
                {
                    name: 'id',
                    component: 'InputHidden',
                    value: allRowData?.id
                },
                {
                    name: 'projectType',
                    component: 'InputHidden',
                    value: 0,
                }
            ]
        },
        {
            cols: 1,
            title: t('employees', 'Empleados'),
            elements: [
                {
                    name: 'Employees',
                    component: 'MultipleCheckboxSelector',
                    subtitles: { subtitleOptions: t('notSelected', 'Sin seleccionar'), subtitleSelectedOptions: t('selected', 'Seleccionados') },
                    items: employees || {},
                    defaultValue: [myEmployee?.Employee?.id] || []
                }
            ]
        },
        {
            cols: 1,
            title: t('groups', 'Grupos'),
            elements: [
                {
                    name: 'Groups',
                    component: 'MultipleCheckboxSelector',
                    subtitles: { subtitleOptions: t('notSelected', 'Sin seleccionar'), subtitleSelectedOptions: t('selected', 'Seleccionados') },
                    items: groups || {},
                }
            ]
        }
    ]
    return (
        <MediumModalCreateEdit url='/api/app/customer/customerProject/createCustomerProject' fields={fields} setOpened={setOpened} onSubmit={onSubmit} stopPropagation={stopPropagation} translations={{
            title: t('createOpportunity', 'Crear oportunidad'),
            button: t('create', 'Crear')
        }} haveButtonPermissions={true} beforeSubmit={handleBeforeSubmit}>
        </MediumModalCreateEdit>
    )
}

interface ModalCreateCustomerOpportunityLogicProps {
    id: number
    customerProjectTypes: DictionaryCustomerProject | null
}

const useModalCreateCustomerOpportunityLogic = ({ id, customerProjectTypes }: ModalCreateCustomerOpportunityLogicProps) => {
    const { myParsedCustomerProjectTypes } = useParseCustomerProjectTypeByStatus({ customerProjectTypes, projectTypeStatus: 'opportunity' })
    const { getCustomerProjectTypeByIsMain } = useGetCustomerProjectTypeByIsMain()
    const customerProjectTypeByIsMain = useMemo(() => getCustomerProjectTypeByIsMain(myParsedCustomerProjectTypes, projectTypesInversed['opportunity'] as ProjectTypesKeys), [myParsedCustomerProjectTypes, getCustomerProjectTypeByIsMain])
    const idProjectType = id !== undefined ? String(id) : ''
    const [myProjectTypeId, setMyProjectTypeId] = useState<string>(idProjectType || customerProjectTypeByIsMain?.id)
    const myProjectStatuses = useMemo(() => {
        return myProjectTypeId ? customerProjectTypes?.[parseInt(myProjectTypeId)].customerProjectStatusDict.opportunity : {}
    }, [myProjectTypeId, customerProjectTypes])

    const handleBeforeSubmit: BeforeSubmitHandler = useCallback((values: any) => {
        if (values.CurrentCProjectStatusId && !myProjectStatuses?.[values.CurrentCProjectStatusId]) {
            values.CurrentCProjectStatusId = null
        }
        return true
    }, [myProjectStatuses])
    
    return { customerProjectTypes, myProjectStatuses, setMyProjectTypeId, handleBeforeSubmit, myParsedCustomerProjectTypes, myProjectTypeId }
}

interface ModalCreateCustomerOpportunityOutsideProps extends ModalCreateCustomerOpportunityOutsiderLogicProps {}

export const ModalCreateCustomerOpportunityOutside: React.FC<ModalCreateCustomerOpportunityOutsideProps> = ({ ...rest }) => {
    const { mySetOpened } = useModalCreateCustomerOpportunityOutsiderLogic({...rest})
    return (
        ReactDOM.createPortal(
            <ModalCreateCustomerOpportunitysConnect {...rest as any} setOpened={mySetOpened} stopPropagation={true} />,
            document.body
        )
    )
}

interface ModalCreateCustomerOpportunityOutsiderLogicProps extends ModalEditComponentProps {
    setLockOnClickOut?: (lock: boolean) => void
}
const useModalCreateCustomerOpportunityOutsiderLogic = ({ setOpened, setLockOnClickOut }: ModalCreateCustomerOpportunityOutsiderLogicProps) => {
    const mySetOpened: ModalOpenedSetTy = useCallback((value) => {
        if (value === null) {
            setLockOnClickOut?.(false)
        }
        setOpened(value)
    }, [setLockOnClickOut, setOpened])

    useEffect(() => {
        setLockOnClickOut?.(true)
    }, [setLockOnClickOut])
    return { mySetOpened }
}

const customerProjectDispatch = {
    setGroups: (payload: GroupPayload) => ({ type: 'CHANGE_GROUP', payload }),
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
    setTaskCFields: (payload: TaskCFieldPayload) => ({ type: 'CHANGE_TASKCFIELD', payload }),
    setCustomerProjectTypes: (payload: CustomerProjectTypePayload) => ({ type: 'CHANGE_CUSTOMER_PROJECT_TYPES', payload }),
    setTChecklists: (payload: TChecklistsPayload) => ({ type: 'CHANGE_T_CHECKLISTS', payload })
}

export type ReduxCustomerEmployeeGroup = ConnectedProps<typeof customerEmployeeGroupConnect>
const mapCustomerEmployeeGroupConnector = (state: AllReduxPayloads) => ({ groups: state.groups, employees: state.employees, taskCFields: state.taskCFields, customerProjectTypes: state.customerProjectTypes, tChecklists: state.tChecklists })
const customerEmployeeGroupConnect = connect(mapCustomerEmployeeGroupConnector, customerProjectDispatch)

const customerEmployeeGroupConnectLoading = withLoading(ModalCreateCustomerOpportunity, [{ fetchUrl: '/api/app/group/getAllGroupsListing', propName: 'groups', setFunctionName: 'setGroups' }, { fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }, { fetchUrl: '/api/app/task/customField/getAllTaskCFieldsComplete', propName: 'taskCFields', setFunctionName: 'setTaskCFields' }, { fetchUrl: '/api/app/customer/customerProject/customerProjectTypes/getAllCustomerProjectTypesFull', propName: 'customerProjectTypes', setFunctionName: 'setCustomerProjectTypes' }, { fetchUrl: '/api/app/config/templateChecklist/getAllTChecklists', propName: 'tChecklists', setFunctionName: 'setTChecklists' }])

const ModalCreateCustomerOpportunitysConnect = customerEmployeeGroupConnect(customerEmployeeGroupConnectLoading)

export default ModalCreateCustomerOpportunitysConnect